// Custom hook for full-screen background coverage
// Ensures background always covers the entire viewport and extends with content

import { useEffect, useMemo } from 'react';
import { getThemeGradient, resolveBackgroundThemeId } from '@/config/appTheme';
import { useVFXConfig } from '@/features/vfx/hooks/useVFXConfig';
import { useVFXConfigContext } from '@/features/vfx/contexts/VFXConfigContext';
import { useStyleHasArt } from '@/hooks/useStyleHasArt';

/**
 * Custom hook that ensures full-screen background coverage
 * Applies min-height: 100vh to the body or a specific element
 * @param targetElement - CSS selector for the target element (default: 'body')
 * @param options - Configuration options
 */
export const useFullScreenBackground = (
    targetElement: string = 'body',
    options: {
        minHeight?: string;
        applyToBody?: boolean;
        customClass?: string;
    } = {}
) => {
    const {
        minHeight = '90vh',
        applyToBody = true,
        customClass = 'full-screen-bg'
    } = options;

    useEffect(() => {
        const element = document.querySelector(targetElement) as HTMLElement;

        if (!element) return;

        // Apply full-screen background styles
        const applyStyles = () => {
            if (applyToBody) {
                document.body.style.minHeight = minHeight;
                document.documentElement.style.minHeight = minHeight;
            }

            if (customClass) {
                element.classList.add(customClass);
            }
        };

        // Apply styles immediately
        applyStyles();

        // Cleanup function
        return () => {
            if (applyToBody) {
                document.body.style.minHeight = '';
                document.documentElement.style.minHeight = '';
            }

            if (customClass) {
                element.classList.remove(customClass);
            }
        };
    }, [targetElement, minHeight, applyToBody, customClass]);
};

/**
 * Hook for applying full-screen background to a specific component
 * Returns CSS classes and styles to apply
 */
export const useFullScreenBackgroundClasses = (options: {
    minHeight?: string;
    className?: string;
} = {}) => {
    const { minHeight = '100vh', className = '' } = options;

    return {
        className: `min-h-screen ${className}`,
        style: {
            minHeight,
        }
    };
};

/**
 * Hook for page-level full-screen background
 * Automatically applies to the current page component
 * Now uses theme from API (background.theme) with fallback to appTheme.ts.
 * Styles without background art (Daylight, Luxe) get no theme gradient: the shell stays
 * transparent over the style's page background (BackgroundLayer).
 * 
 * @param options - Configuration options
 * @param options.minHeight - Minimum height (default: '90vh')
 * @param options.gradient - Optional custom gradient (overrides theme)
 * @param options.className - Additional CSS classes
 */
export const usePageFullScreenBackground = (options: {
    minHeight?: string;
    gradient?: string;
    className?: string;
} = {}) => {
    const { backgroundConfig, configVersion } = useVFXConfig();
    const { state } = useVFXConfigContext();
    const styleHasArt = useStyleHasArt();

    // Get theme from API: prefer app_themes, then background.theme (so schedule/default theme applies)
    // Single useMemo to keep hook order stable (Rules of Hooks)
    const defaultGradient = useMemo(() => {
        if (!styleHasArt) return '';
        return getThemeGradient(resolveBackgroundThemeId(state.config as Parameters<typeof resolveBackgroundThemeId>[0]));
    }, [styleHasArt, state.config?.app_themes, state.config?.background?.theme]);

    const {
        minHeight = '90vh',
        gradient = defaultGradient, // Use theme from API with fallback
        className = ''
    } = options;

    // Recalculate className when gradient changes
    const finalClassName = useMemo(() => {
        return `min-h-screen ${gradient} ${className}`;
    }, [gradient, className]);

    return {
        className: finalClassName,
        style: {
            minHeight,
        }
    };
};

export default useFullScreenBackground;
